import { Avatar } from './Avatar';
import { CreditsMeter } from './CreditsMeter';
import { MenuRow } from './MenuRow';
import { MenuSpacer, MenuSurface } from './MenuSurface';
import type { AccountAction, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';
import { offersMacApp } from '~/lib/macApp';

export interface AccountMenuProps {
  name?: string;
  avatarUrl?: string;
  /** Omit to drop the meter entirely (e.g. an account with no credit plan). */
  credits?: { used: number; total: number; topup?: number; loading?: boolean };
  onAction?: (action: AccountAction) => void;
  theme?: HeaderTheme;
  className?: string;
}

/** 276px wide, anchored right edge to the avatar slot's right edge. */
export const ACCOUNT_MENU_WIDTH = 276;

/**
 * Order is fixed and matches `AvatarDropdownUI.tsx`: Profile, Billing, Brand Kits, Integrations,
 * Settings, Sign Out. What changes in v1.5 is the top — the credits readout is a meter above the
 * rows, and Sign Out is set apart by a 4px spacer instead of the old divider. On a Mac, outside the Mac
 * app, Download Mac App sits right after Integrations.
 */
export function AccountMenu({
  name = 'User',
  avatarUrl,
  credits,
  onAction,
  theme: themeProp,
  className,
}: AccountMenuProps) {
  const theme = useV15Theme(themeProp);
  return (
    <MenuSurface width={ACCOUNT_MENU_WIDTH} theme={theme} label="Account" className={className}>
      {credits ? (
        <>
          <CreditsMeter
            used={credits.used}
            total={credits.total}
            topup={credits.topup}
            loading={credits.loading}
            theme={theme}
          />
          <MenuSpacer />
        </>
      ) : null}

      <MenuRow
        label="Profile"
        theme={theme}
        // The profile row leads with the user's own face rather than a generic glyph — it is the
        // one row that identifies rather than navigates.
        leading={<Avatar src={avatarUrl} name={name} size={16} interactive={false} theme={theme} />}
        onClick={() => onAction?.('profile')}
      />
      <MenuRow icon="bank-card" label="Billing" theme={theme} onClick={() => onAction?.('billing')} />
      <MenuRow icon="brand-kit" label="Brand Kits" theme={theme} onClick={() => onAction?.('brand-kits')} />
      {/* Not in `AvatarDropdownUI.tsx`: Skills arrived after that order was fixed, and sits with the
          other thing the agent reads from the account, right under Brand Kits. */}
      <MenuRow icon="cook-book" label="Skills" theme={theme} onClick={() => onAction?.('skills')} />
      <MenuRow
        icon="Integrations"
        label="Integrations"
        theme={theme}
        onClick={() => onAction?.('integrations')}
      />
      {offersMacApp() ? (
        <MenuRow icon="apple" label="Download Mac App" theme={theme} onClick={() => onAction?.('download-mac')} />
      ) : null}
      <MenuRow icon="settings" label="Settings" theme={theme} onClick={() => onAction?.('settings')} />
      <MenuRow
        icon="logout-box-r"
        label="Sign Out"
        destructive
        theme={theme}
        onClick={() => onAction?.('sign-out')}
      />
    </MenuSurface>
  );
}
